import { Button } from "@/components/ui/button";
import { useAcknowledgeDisclosure, useDisclosure } from "@/hooks";
import { Check, Eye, EyeOff } from "lucide-react";
import type { ReactNode } from "react";

/**
 * Shown once, before a member reaches the product, when they hold an org seat.
 *
 * This is not a formality. Named-employee reporting is defensible when the
 * person has been told and indefensible when they have not, so this screen is
 * what makes the People view legitimate rather than surveillance. It costs one
 * screen.
 *
 * The copy states BOTH halves plainly. Listing only what is shared reads as a
 * warning; listing only what is private reads as reassurance. A person deciding
 * how to use a work account needs both to be true and visible.
 */
export function DisclosureGate({ children }: { children: ReactNode }) {
	const { data, isLoading } = useDisclosure();
	const ack = useAcknowledgeDisclosure();

	// Never block the app on this call failing — a broken disclosure fetch
	// should not lock someone out of their own dashboard.
	if (isLoading || !data || data.acknowledged_at) return <>{children}</>;

	return (
		<div className="flex min-h-screen items-center justify-center bg-surface2 px-6 py-12">
			<div className="w-full max-w-lg rounded-lg border border-divider bg-surface-primary p-8">
				<h1 className="text-h5 font-semibold tracking-tight text-heading">
					Before you start
				</h1>
				<p className="mt-2 text-body text-muted-foreground">
					You're using Curo through{" "}
					<strong className="text-heading">{data.organization}</strong>. Here's
					exactly what they can and cannot see.
				</p>

				<div className="mt-6 grid gap-5">
					<section>
						<div className="mb-2 flex items-center gap-2">
							<Eye className="size-4 text-orange-text" />
							<h2 className="text-body font-semibold text-heading">
								{data.organization} can see
							</h2>
						</div>
						<ul className="flex flex-col gap-1.5 pl-6">
							{data.shared.map((item) => (
								<li key={item} className="list-disc text-body text-paragraph">
									{item}
								</li>
							))}
						</ul>
					</section>

					<section>
						<div className="mb-2 flex items-center gap-2">
							<EyeOff className="size-4 text-success" />
							<h2 className="text-body font-semibold text-heading">
								They cannot see
							</h2>
						</div>
						<ul className="flex flex-col gap-1.5 pl-6">
							{data.not_shared.map((item) => (
								<li key={item} className="list-disc text-body text-paragraph">
									{item}
								</li>
							))}
						</ul>
					</section>
				</div>

				<div className="mt-7 flex items-center gap-3">
					<Button onClick={() => ack.mutate()} disabled={ack.isPending}>
						{ack.isPending ? (
							"Saving…"
						) : (
							<>
								<Check className="size-4" />
								Got it
							</>
						)}
					</Button>
					<span className="text-caption text-muted-foreground">
						You'll only see this once.
					</span>
				</div>
			</div>
		</div>
	);
}
